import type { Task, TaskStatus } from "openapi/api/model"

/**
 * The Ops board's arithmetic, kept out of the component.
 *
 * Canvas 33a replaces the four stat cards this page used to lead with — Total,
 * In Progress, Blocked, Completed — with one receipts line under the title:
 * "10 open · 2 blocked · 2 overdue · 4 due this week · 2 closed since Monday".
 * Three of the four cards were already legible as the column counts beside each
 * heading, so the line says the two things the columns cannot — what is late,
 * and what is landing — and stops repeating the two they can.
 *
 * Everything here is a pure function of the task list and a clock, so the
 * figures can be checked without a browser.
 */

/** The board's columns, left to right. Matches the API's `status` enum. */
export const TASK_COLUMNS = ["todo", "inProgress", "blocked", "done"] as const
export type ColumnId = (typeof TASK_COLUMNS)[number]

export const COLUMN_TITLE: Record<ColumnId, string> = {
  todo: "To-Do",
  inProgress: "In Progress",
  blocked: "Blocked",
  done: "Done",
}

/**
 * A column's colour, taken from the app's one semantic ramp rather than from
 * four new hex values.
 *
 * 33a draws In Progress, Blocked and Done in exactly the ramp's `screening`,
 * `rejected` and `hired` — the designer reused it — so the mapping is already
 * the artboard's intent. To-Do is drawn a little deeper than `shortlist`; the
 * token wins, because the ramp is the only colour set in the product that has
 * a light theme as well as a dark one, and a status that keeps its meaning
 * through a theme switch is worth more than four pixels of hue.
 */
export const COLUMN_STAGE: Record<ColumnId, string> = {
  todo: "shortlist",
  inProgress: "screening",
  blocked: "rejected",
  done: "hired",
}

/** A task's id, under either of the two names the API answers with. */
export const taskId = (task: Task): string | undefined => task.id ?? task.task_id

/**
 * A task's title. `GET /tasks` sends `task_title` on some rows and `title` on
 * others, so both are read; a task with neither is still a task, and says so
 * rather than rendering an empty line.
 */
/**
 * The title a task actually carries, under either name, or "" when it has
 * none. An editor initialises from this: `taskTitle`'s fallback is a thing to
 * show, and writing it back would give the task a title it never had.
 */
export const rawTaskTitle = (task: Task): string =>
  (task as Task & { task_title?: string }).task_title?.trim() || task.title?.trim() || ""

/** What a task is called on screen, including when it is called nothing. */
export const taskTitle = (task: Task): string => rawTaskTitle(task) || "Untitled task"

const DAY = 86_400_000

/** Midnight local, so "today" is a date rather than a moment. */
function startOfDay(at: Date): number {
  const copy = new Date(at)
  copy.setHours(0, 0, 0, 0)
  return copy.getTime()
}

/**
 * A date-or-timestamp string as local midnight on the day it names.
 *
 * `Date.parse("2026-09-14")` is midnight **UTC**, so anywhere behind UTC that
 * is the previous evening — a task closed on Monday then counted as closed the
 * Sunday before, and dropped out of "closed since Monday" for the whole week.
 * A date-only string gets an explicit local time; a full timestamp already
 * names a moment and is left alone.
 */
function parseDay(value: string | null | undefined): number | undefined {
  if (!value) return undefined
  const parsed = Date.parse(value.length <= 10 ? `${value}T00:00:00` : value)
  return Number.isFinite(parsed) ? startOfDay(new Date(parsed)) : undefined
}

/**
 * Whole days from today to a due date: negative is late, 0 is today.
 *
 * Both sides are floored to midnight first. Comparing a date-only `due_date`
 * against a timestamp made every task due today read as overdue from 00:01
 * onwards, which is how the old board came to show an overdue triangle on work
 * that had all day left.
 */
export function daysUntil(due: string | null | undefined, now = new Date()): number | undefined {
  const day = parseDay(due)
  if (day === undefined) return undefined
  return Math.round((day - startOfDay(now)) / DAY)
}

const MONTH = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]

/** "22 Sep" — the board's date format, per 33a. */
export function shortDate(iso: string | null | undefined): string | undefined {
  const day = parseDay(iso)
  if (day === undefined) return undefined
  const at = new Date(day)
  return `${at.getDate()} ${MONTH[at.getMonth()]}`
}

export type DueTone = "overdue" | "today" | "quiet"

/**
 * What a card says about its own deadline, and how loudly.
 *
 * 33a gives four readings: "due today" in amber, "4 days overdue" in red,
 * a plain "22 Sep" in grey, and "closed 18 Sep" on a done card. A task with no
 * due date says nothing at all rather than printing a dash, because a dash in
 * the date slot reads as a date that failed to load.
 */
export function dueLabel(
  task: Task,
  now = new Date(),
): { text: string; tone: DueTone } | undefined {
  if (task.status === "done") {
    const closed = shortDate(task.completion_date ?? undefined)
    return closed ? { text: `closed ${closed}`, tone: "quiet" } : undefined
  }

  const days = daysUntil(task.due_date, now)
  if (days === undefined) return undefined
  if (days < 0) {
    const late = Math.abs(days)
    return { text: `${late} day${late === 1 ? "" : "s"} overdue`, tone: "overdue" }
  }
  if (days === 0) return { text: "due today", tone: "today" }
  return { text: shortDate(task.due_date) ?? "", tone: "quiet" }
}

/** What a card names beneath its priority — the job, the candidate, or neither. */
export function taskSubject(task: Task): string | undefined {
  return task.job_title?.trim() || task.candidate_name?.trim() || undefined
}

export interface TaskReceipts {
  open: number
  blocked: number
  overdue: number
  dueThisWeek: number
  closedSinceMonday: number
}

/**
 * The line under the title.
 *
 * "Open" is everything not done, blocked included — a blocked task is still
 * work you carry. "This week" runs to the coming Sunday rather than seven days
 * out, so the figure means the same thing on Friday as it does on Monday.
 */
export function receiptsOf(tasks: Task[], now = new Date()): TaskReceipts {
  const today = startOfDay(now)
  /*
    Monday of the current week, and the Sunday that closes it — by calendar
    date, not by subtracting 24-hour days. A day is not always 24 hours: in a
    zone whose clocks change at midnight this returned Monday at 01:00, and
    `parseDay` floors every completion to midnight, so everything closed that
    Monday fell outside "closed since Monday". America/Santiago, Sunday 5
    April 2026 is such a case; the calendar grid had the same bug.
  */
  const weekday = (new Date(today).getDay() + 6) % 7
  const mondayDate = new Date(today)
  mondayDate.setDate(mondayDate.getDate() - weekday)
  const monday = startOfDay(mondayDate)
  const sundayDate = new Date(monday)
  sundayDate.setDate(sundayDate.getDate() + 6)
  const sunday = startOfDay(sundayDate)

  let open = 0
  let blocked = 0
  let overdue = 0
  let dueThisWeek = 0
  let closedSinceMonday = 0

  for (const task of tasks) {
    if (task.status === "done") {
      const closed = parseDay(task.completion_date)
      if (closed !== undefined && closed >= monday) closedSinceMonday += 1
      continue
    }

    open += 1
    if (task.status === "blocked") blocked += 1

    /*
      The due day is compared with the week's bounds directly. Going through a
      day count and rebuilding the date as `today + days * DAY` reintroduced
      the bug the Monday/Sunday comment above describes, one level down: in a
      week where the clocks go forward the week is 143 hours, not 144, so a
      task six calendar days out landed at Sunday 01:00 and fell outside a
      Sunday that is midnight. Asia/Jerusalem, the week of Monday 23 March
      2026, is such a case. `parseDay` and `startOfDay` both return a real
      calendar midnight, so comparing them needs no arithmetic at all.
    */
    const due = parseDay(task.due_date)
    if (due === undefined) continue
    if (due < today) overdue += 1
    else if (due <= sunday) dueThisWeek += 1
  }

  return { open, blocked, overdue, dueThisWeek, closedSinceMonday }
}

export type Board = Record<ColumnId, string[]>

/** Task ids per column, in the order the API sent them. */
export function buildBoard(tasks: Task[]): Board {
  const board: Board = { todo: [], inProgress: [], blocked: [], done: [] }
  for (const task of tasks) {
    const id = taskId(task)
    if (!id) continue
    const status = (task.status ?? "todo") as TaskStatus
    const column: ColumnId = TASK_COLUMNS.includes(status as ColumnId)
      ? (status as ColumnId)
      : "todo"
    board[column].push(id)
  }
  return board
}

/** Whether two boards hold the same ids in the same order. */
export function sameBoard(a: Board, b: Board): boolean {
  return TASK_COLUMNS.every(
    (column) =>
      a[column].length === b[column].length &&
      a[column].every((id, index) => id === b[column][index]),
  )
}
